import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../lib/auth'
import { familyOverview, postFamilyNote } from '../../lib/api'

// `shift_date` / `receipt_date` are Postgres `date` columns, so they can arrive
// as a bare 'YYYY-MM-DD' or as a full ISO timestamp — slicing handles both and
// keeps the day from shifting across timezones.
const fmtD = (iso) => {
  if (!iso) return ''
  try { return new Date(String(iso).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' }) } catch (e) { return iso }
}
const fmtT = (iso) => {
  if (!iso) return ''
  try { return new Date(iso).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' }) } catch (e) { return iso }
}
const fmtDT = (iso) => {
  if (!iso) return ''
  try { return new Date(iso).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) } catch (e) { return iso }
}
const fmtRange = (start, end) => [start, end].filter(Boolean).join(' – ')
const fmtMoney = (cents) => (cents === null || cents === undefined ? '—' : '$' + (cents / 100).toFixed(2))

const textareaStyle = {
  fontFamily: 'Inter, Arial, sans-serif', fontSize: 16, color: '#191917', background: '#FFFFFF',
  border: '1px solid #D0C9B7', borderRadius: 4, padding: '12px 14px', width: '100%', maxWidth: '100%', boxSizing: 'border-box',
}

export default function FamilyPortal() {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const [client, setClient] = useState(null)
  const [shiftRows, setShiftRows] = useState([])
  const [familyNotes, setFamilyNotes] = useState([])
  const [receipts, setReceipts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tab, setTab] = useState('notes')
  const [newNote, setNewNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [noteError, setNoteError] = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await familyOverview()
        if (cancelled) return
        setClient(data.client || null)
        setShiftRows(data.shifts || [])
        setFamilyNotes(data.familyNotes || [])
        setReceipts(data.receipts || [])
      } catch (err) {
        if (!cancelled) setError(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const logout = async () => { await signOut(); navigate('/login') }

  const header = (
    <div style={{ background: '#003F3A', color: '#FAF7F0', padding: '14px 24px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 21, fontWeight: 600 }}>Family Room Portal</span>
        {!!client && <span style={{ fontSize: 14, opacity: 0.8 }}>· Care for {client.full_name}</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, fontSize: 15 }}>
          <span>Signed in as <strong>{user ? user.fullName : 'Guest'}</strong></span>
          <button type="button" onClick={logout} style={{ background: 'transparent', color: '#B7C4B7', border: '1px solid #527569', borderRadius: 4, padding: '8px 14px', fontWeight: 600, fontSize: 14, fontFamily: 'Inter, Arial, sans-serif', cursor: 'pointer' }}>Sign out</button>
        </div>
      </div>
    </div>
  )

  const message = (text) => (
    <div style={{ minHeight: '100vh', background: '#F4EDDF', color: '#191917', fontFamily: 'Inter, Arial, sans-serif', fontSize: 17, lineHeight: 1.6 }}>
      {header}
      <div style={{ maxWidth: 'min(520px, calc(100% - 48px))', width: '100%', boxSizing: 'border-box', margin: '80px auto', textAlign: 'center', color: '#5D645B' }}>{text}</div>
    </div>
  )

  if (loading) return message('Loading your family room…')
  if (error) return message(error)

  const shifts = shiftRows.map((s) => ({
    id: s.id,
    dateLabel: fmtD(s.shift_date),
    time: fmtRange(s.start_time, s.end_time),
    caregiver: s.caregiver_name || 'To be assigned',
    checkedIn: s.checked_in_at ? fmtT(s.checked_in_at) : '—',
    note: s.note || '—',
    outLabel: s.checked_out_at ? '· Out: ' + fmtT(s.checked_out_at) : '',
    badge: s.checked_out_at ? 'Completed' : 'In progress',
    badgeBg: s.checked_out_at ? '#B7C4B7' : '#00564F',
    badgeFg: s.checked_out_at ? '#003F3A' : '#FAF7F0',
    marks: (s.tasks || []).map((t) => ({ ...t, mark: t.done ? '✓' : '○', markColor: t.done ? '#6B8B75' : '#D0C9B7', color: t.done ? '#191917' : '#5D645B' })),
  }))

  const addNote = async () => {
    const text = newNote.trim()
    if (!text || busy) return
    setBusy(true)
    setNoteError('')
    try {
      const { note } = await postFamilyNote(text)
      setFamilyNotes((prev) => [note, ...prev])
      setNewNote('')
    } catch (err) {
      setNoteError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const tabDefs = [['notes', 'Daily Care Notes'], ['schedule', 'Schedule'], ['familyNotes', 'Family Notes'], ['billing', 'Billing Receipts']]

  return (
    <div style={{ minHeight: '100vh', background: '#F4EDDF', color: '#191917', fontFamily: 'Inter, Arial, sans-serif', fontSize: 17, lineHeight: 1.6 }}>
      {header}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px 64px 24px' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
          {tabDefs.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              style={{
                background: tab === key ? '#00564F' : '#FAF7F0', color: tab === key ? '#FAF7F0' : '#191917',
                border: tab === key ? '1px solid #00564F' : '1px solid #D0C9B7',
                borderRadius: 4, padding: '12px 18px', minHeight: 48, fontWeight: 600, fontSize: 15, fontFamily: 'Inter, Arial, sans-serif', cursor: 'pointer',
              }}
            >{label}</button>
          ))}
        </div>

        {tab === 'notes' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {shifts.map((s) => (
              <div key={s.id} style={{ background: '#FAF7F0', border: '1px solid #D0C9B7', borderRadius: 8, padding: 'clamp(18px, 4vw, 28px)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 600, color: '#003F3A' }}>{s.dateLabel} · {s.time}</div>
                    <div style={{ fontSize: 15, color: '#5D645B' }}>Caregiver: {s.caregiver} · In: {s.checkedIn} {s.outLabel}</div>
                  </div>
                  <span style={{ background: s.badgeBg, color: s.badgeFg, borderRadius: 4, padding: '6px 12px', fontSize: 14, fontWeight: 600, height: 'fit-content' }}>{s.badge}</span>
                </div>
                <div className="grid grid-2" style={{ gap: 8, marginBottom: 14 }}>
                  {s.marks.map((t) => (
                    <div key={t.id} style={{ display: 'flex', gap: 10, alignItems: 'center', minHeight: 32, fontSize: 15, color: t.color }}>
                      <span style={{ fontWeight: 700, color: t.markColor }}>{t.mark}</span>{t.label}
                    </div>
                  ))}
                </div>
                <div style={{ background: '#EEE8DA', borderRadius: 6, padding: '14px 16px', fontSize: 15 }}><strong>Caregiver note:</strong> {s.note}</div>
              </div>
            ))}
          </div>
        )}

        {tab === 'schedule' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {shifts.map((s) => (
              <div key={s.id} style={{ background: '#FAF7F0', border: '1px solid #D0C9B7', borderRadius: 8, padding: '20px 24px', display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                  <div style={{ fontWeight: 600, color: '#003F3A' }}>{s.dateLabel} · {s.time}</div>
                  <div style={{ fontSize: 15, color: '#5D645B' }}>Caregiver: {s.caregiver}</div>
                </div>
                <span style={{ background: s.badgeBg, color: s.badgeFg, borderRadius: 4, padding: '6px 12px', fontSize: 14, fontWeight: 600 }}>{s.badge === 'Completed' ? 'Completed' : 'Scheduled'}</span>
              </div>
            ))}
          </div>
        )}

        {tab === 'familyNotes' && (
          <div className="grid" style={{ '--cols': '1.2fr 1fr', gap: 24, alignItems: 'start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
              {familyNotes.map((n) => (
                <div key={n.id} style={{ background: '#FAF7F0', border: '1px solid #D0C9B7', borderRadius: 8, padding: '20px 24px' }}>
                  <div style={{ fontSize: 14, color: '#527569', fontWeight: 600, marginBottom: 6 }}>{fmtDT(n.created_at)} · {n.author_name}</div>
                  <div style={{ fontSize: 16, overflowWrap: 'anywhere' }}>{n.body}</div>
                </div>
              ))}
            </div>
            <div style={{ background: '#FFFFFF', border: '1px solid #D0C9B7', borderRadius: 8, padding: 'clamp(18px, 4vw, 24px)', minWidth: 0 }}>
              <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 600, color: '#003F3A', margin: '0 0 12px 0' }}>Add a note for caregivers</h2>
              <textarea
                rows={4}
                placeholder="e.g. reminders about meals, appointments, or routine changes"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                style={textareaStyle}
              />
              <button
                type="button"
                onClick={addNote}
                disabled={busy}
                style={{ marginTop: 14, background: '#00564F', color: '#FFFFFF', border: 'none', borderRadius: 4, padding: '13px 22px', minHeight: 48, fontWeight: 600, fontSize: 16, fontFamily: 'Inter, Arial, sans-serif', cursor: 'pointer' }}
              >Share Note</button>
              {!!noteError && <p style={{ fontSize: 14, color: '#8A3B2E', margin: '12px 0 0 0' }}>{noteError}</p>}
              <p style={{ fontSize: 13, color: '#5D645B', margin: '12px 0 0 0' }}>Family notes are helpful reminders — medical instructions come from healthcare professionals.</p>
            </div>
          </div>
        )}

        {tab === 'billing' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {receipts.map((b) => (
              <div key={b.id} style={{ background: '#FAF7F0', border: '1px solid #D0C9B7', borderRadius: 8, padding: '20px 24px', display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                  <div style={{ fontWeight: 600, color: '#003F3A' }}>Receipt · {fmtD(b.receipt_date)}</div>
                  <div style={{ fontSize: 15, color: '#5D645B' }}>{b.description}{b.hours ? ` (${b.hours} hrs)` : ''}</div>
                </div>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <span style={{ background: '#B7C4B7', color: '#003F3A', borderRadius: 4, padding: '6px 12px', fontSize: 14, fontWeight: 600, textTransform: 'capitalize' }}>{b.status}</span>
                  <span style={{ fontWeight: 600, color: '#527569', fontSize: 15 }}>{fmtMoney(b.amount_cents)}</span>
                </div>
              </div>
            ))}
            <p style={{ fontSize: 14, color: '#5D645B', margin: '8px 0 0 0' }}>Receipts and rates are confirmed in your care agreement — amounts shown once billing rules are finalized.</p>
          </div>
        )}
      </div>
    </div>
  )
}
